import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatError, resolveImageUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import {
  Gift, Trophy, Users, Clock, Check, X, Crown, Lock, Calendar, CalendarBlank,
  UserCircle, ShieldCheck, CheckCircle, CaretRight, UserPlus, UserMinus, MagnifyingGlass,
} from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" });
}
function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString("pt-PT", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " às " + d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
}

function statusInfo(g) {
  const drawDt = new Date(g.draw_at);
  const now = new Date();
  if (g.status === "completed") return { label: "TERMINADO", color: "#10B981", canJoin: false };
  if (g.status === "drawn") return { label: "AGUARDA VALIDAÇÃO", color: "#F59E0B", canJoin: false };
  if (drawDt <= now) return { label: "A SORTEAR", color: "#F59E0B", canJoin: false };
  return { label: "ABERTO", color: "#E63946", canJoin: true };
}

function daysBetween(iso) {
  if (!iso) return null;
  const ms = new Date(iso) - new Date();
  if (ms <= 0) return 0;
  return Math.ceil(ms / (1000 * 60 * 60 * 24));
}

/* ============ LIST PAGE ============ */
export default function Sorteios() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/giveaways").then(r => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  return (
    <div className="pt-20 max-w-6xl mx-auto px-5 lg:px-8 pb-24" data-testid="sorteios-page">
      <div className="flex items-center gap-4 mb-8">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#E63946] to-[#B91C1C] grid place-items-center flex-shrink-0">
          <Gift size={26} weight="fill" className="text-white" />
        </div>
        <div>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tight">Sorteio de Manga</h1>
          <p className="text-zinc-400 mt-1">Participa e tenha a oportunidade de ganhar mangás incríveis!</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-16 text-zinc-500 font-mono-accent text-xs">A CARREGAR...</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-white/10 rounded-2xl py-20 text-center">
          <Gift size={40} weight="duotone" className="text-zinc-500 mx-auto mb-3" />
          <div className="font-display font-bold text-xl">Sem sorteios ativos</div>
          <div className="text-sm text-zinc-500 mt-1">Volta em breve para ver novidades.</div>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4" data-testid="sorteios-list">
          {items.map(g => {
            const s = statusInfo(g);
            return (
              <Link key={g.giveaway_id} to={`/sorteio/${g.giveaway_id}`} className="block bg-[#0f0f14] border border-white/5 rounded-2xl overflow-hidden hover:border-[#E63946]/40 transition-all" data-testid={`sorteio-${g.giveaway_id}`}>
                {g.image_url && (
                  <div className="aspect-[16/8] bg-black/30 overflow-hidden">
                    <img src={resolveImageUrl(g.image_url)} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono-accent font-bold tracking-wider" style={{ backgroundColor: `${s.color}20`, color: s.color }}>
                      {s.label}
                    </span>
                    {g.sequence_number && (
                      <span className="text-[10px] text-zinc-500 font-mono-accent">SORTEIO #{g.sequence_number}</span>
                    )}
                  </div>
                  <h2 className="font-display font-extrabold text-xl leading-tight">{g.title}</h2>
                  {g.prize && <p className="text-sm text-[#E63946] mt-1 font-display font-semibold">{g.prize}</p>}
                  {g.description && <p className="text-sm text-zinc-400 mt-2 line-clamp-2">{g.description}</p>}
                  <div className="mt-4 flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-zinc-400">
                      <Users size={14} weight="bold" />
                      <span>{g.participant_count}{g.max_participants > 0 ? ` / ${g.max_participants}` : ""} participantes</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 font-mono-accent flex items-center gap-1">
                      <Clock size={11} weight="bold" /> {fmtDate(g.draw_at)}
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ============ DETAIL PAGE ============ */
export function SorteioDetail() {
  const [g, setG] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [memberQuery, setMemberQuery] = useState("");
  const [members, setMembers] = useState([]);
  const { user } = useAuth();
  const navigate = useNavigate();
  const id = window.location.pathname.split("/").pop();

  async function load() {
    try {
      const { data } = await api.get(`/giveaways/${id}`);
      setG(data);
    } catch (e) { toast.error("Sorteio não encontrado"); navigate("/sorteio"); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  // Search members when admin dialog open
  useEffect(() => {
    if (!addMemberOpen) return;
    const t = setTimeout(async () => {
      const { data } = await api.get("/members", { params: memberQuery ? { q: memberQuery, limit: 30 } : { limit: 30 } });
      setMembers(data);
    }, 250);
    return () => clearTimeout(t);
  }, [addMemberOpen, memberQuery]);

  useEffect(() => {
    if (!g) return;
    const s = statusInfo(g);
    if (s.label === "A SORTEAR" || g.status === "drawn") {
      const t = setInterval(() => load(), 10000);
      return () => clearInterval(t);
    }
    const ms = new Date(g.draw_at) - new Date();
    if (ms > 0 && ms < 60000) {
      const t = setTimeout(() => load(), ms + 500);
      return () => clearTimeout(t);
    }
  }, [g]); // eslint-disable-line

  async function join() {
    if (!user) { navigate("/registar"); return; }
    setBusy(true);
    try {
      await api.post(`/giveaways/${id}/join`);
      toast.success("Estás a participar!");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  async function adminAddParticipant(userId) {
    try {
      await api.post(`/giveaways/${id}/participants/${userId}`);
      toast.success("Membro adicionado ao sorteio");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  async function adminRemoveParticipant(userId) {
    if (!confirm("Remover este participante do sorteio?")) return;
    try {
      await api.delete(`/giveaways/${id}/participants/${userId}`);
      toast.success("Participante removido");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  }

  async function approve() {
    if (!confirm("Aprovar este vencedor?")) return;
    setBusy(true);
    try {
      await api.post(`/giveaways/${id}/approve`);
      toast.success("Vencedor aprovado!");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  async function reject() {
    if (!rejectReason.trim()) { toast.error("Indica um motivo"); return; }
    setBusy(true);
    try {
      await api.post(`/giveaways/${id}/reject`, { reason: rejectReason.trim() });
      toast.success("Vencedor recusado, novo vencedor selecionado");
      setRejectOpen(false); setRejectReason("");
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  }

  if (loading) return <div className="pt-32 text-center text-zinc-500 font-mono-accent text-xs">A CARREGAR...</div>;
  if (!g) return null;
  const s = statusInfo(g);
  const isAdmin = user?.role === "admin";
  const daysLeft = daysBetween(g.draw_at);

  // Split title into first + second line if separator present, else show whole title
  const titleParts = g.title?.split(" — ") || g.title?.split(" - ") || [g.title];
  const titleMain = titleParts[0];
  const titleSub = titleParts.length > 1 ? titleParts.slice(1).join(" — ") : "";

  return (
    <div className="pt-16 max-w-6xl mx-auto px-3 lg:px-6 pb-24" data-testid="sorteio-detail">
      {/* Breadcrumb */}
      <div className="text-sm text-zinc-500 mb-4 pt-2 flex items-center gap-1">
        <Link to="/" className="hover:text-white">Início</Link>
        <CaretRight size={12} weight="bold" />
        <Link to="/sorteio" className="hover:text-white">Sorteios</Link>
      </div>

      {/* Page header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#E63946] to-[#B91C1C] grid place-items-center flex-shrink-0">
          <Gift size={26} weight="fill" className="text-white" />
        </div>
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight">Sorteio de Manga</h1>
          <p className="text-zinc-400 mt-0.5 text-sm">Participe e tenha a oportunidade de ganhar mangás incríveis!</p>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr_360px] gap-5">
        {/* MAIN CARD */}
        <div className="bg-gradient-to-br from-[#141018] via-[#0f0f14] to-[#1a0b0f] border border-white/5 rounded-2xl p-6 lg:p-8">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-mono-accent font-bold tracking-wider" style={{ backgroundColor: `${s.color}20`, color: s.color }} data-testid="sorteio-status">
              {s.label}
            </span>
            {g.sequence_number && (
              <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs font-mono-accent font-bold tracking-wider bg-[#E63946] text-white" data-testid="sorteio-sequence">
                SORTEIO #{g.sequence_number}
              </span>
            )}
          </div>

          <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
            <div>
              {g.prize && (
                <>
                  <div className="font-display text-[#E63946] text-lg font-bold mb-2">Prémio</div>
                  <h2 className="font-display text-3xl md:text-4xl font-extrabold leading-tight" data-testid="sorteio-prize">{g.prize}</h2>
                </>
              )}
              {!g.prize && (
                <h2 className="font-display text-3xl md:text-4xl font-extrabold leading-tight" data-testid="sorteio-title">{titleMain}{titleSub && <span className="block text-2xl font-bold text-zinc-300 mt-1">{titleSub}</span>}</h2>
              )}
              {g.description && <p className="mt-4 text-zinc-300 whitespace-pre-line leading-relaxed max-w-md">{g.description}</p>}
            </div>
            {g.image_url && (
              <div className="w-full max-w-sm mx-auto md:mx-0">
                <img src={resolveImageUrl(g.image_url)} alt="" className="w-full h-auto rounded-xl object-contain" data-testid="sorteio-image" />
              </div>
            )}
          </div>

          {/* Stats row */}
          <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4 pt-6 border-t border-white/5">
            <MiniStat icon={<Users size={20} weight="bold" />} value={g.participant_count} label="Participantes" testid="stat-participants" />
            <MiniStat icon={<Clock size={20} weight="bold" />} value={daysLeft !== null ? `${daysLeft} dias` : "—"} label="Tempo restante" testid="stat-timeleft" />
            <MiniStat icon={<CalendarBlank size={20} weight="bold" />} value={fmtDate(g.draw_at)} label="Termina em" testid="stat-endson" />
            {g.prize ? (
              <MiniStat icon={<Trophy size={20} weight="bold" />} value={g.prize.split(" — ")[0].split(" - ")[0]} sub={g.prize.split(" — ")[1] || g.prize.split(" - ")[1] || ""} label="Prémio" testid="stat-prize" />
            ) : (
              <MiniStat icon={<Trophy size={20} weight="bold" />} value="—" label="Prémio" testid="stat-prize" />
            )}
          </div>

          {/* Winner card */}
          {g.selected_winner && (
            <div className="mt-6 rounded-xl border p-5 bg-gradient-to-br from-[#F59E0B]/10 via-[#E63946]/5 to-[#0f0f14] border-[#F59E0B]/30" data-testid="selected-winner">
              <div className="font-mono-accent text-[10px] text-[#F59E0B] mb-2 flex items-center gap-1">
                <Crown size={12} weight="fill" /> {g.status === "completed" ? "VENCEDOR" : "VENCEDOR SELECIONADO — AGUARDA VALIDAÇÃO"}
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                {g.selected_winner.picture ? (
                  <img src={resolveImageUrl(g.selected_winner.picture)} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-[#F59E0B]" />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-lg font-display font-bold border-2 border-[#F59E0B]">
                    {(g.selected_winner.name || "?")[0]?.toUpperCase()}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <Link to={`/membros/${g.selected_winner.slug || g.selected_winner.user_id}`} className="font-display font-extrabold text-xl hover:text-[#F59E0B]">
                    {g.selected_winner.name}
                  </Link>
                  <div className="text-[11px] text-zinc-500 font-mono-accent mt-0.5">Sorteado em {fmtDateTime(g.selected_winner.drawn_at)}</div>
                </div>
                {isAdmin && g.status === "drawn" && (
                  <div className="flex gap-2">
                    <Button onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-winner-btn">
                      <Check size={16} weight="bold" className="mr-1" /> Aprovar
                    </Button>
                    <Button onClick={() => setRejectOpen(true)} disabled={busy} variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10" data-testid="reject-winner-btn">
                      <X size={16} weight="bold" className="mr-1" /> Recusar
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rejected history */}
          {g.rejected_history?.length > 0 && (
            <div className="mt-4" data-testid="rejected-history">
              <div className="font-mono-accent text-[10px] text-zinc-500 mb-2">HISTÓRICO DE RECUSAS ({g.rejected_history.length})</div>
              <div className="space-y-2">
                {g.rejected_history.map((r, i) => (
                  <div key={i} className="flex items-start gap-3 bg-[#1C1C22] border border-red-500/10 rounded-lg p-3" data-testid={`rejected-${i}`}>
                    {r.picture ? (
                      <img src={resolveImageUrl(r.picture)} alt="" className="w-9 h-9 rounded-full object-cover" />
                    ) : (
                      <div className="w-9 h-9 rounded-full bg-zinc-800 grid place-items-center text-xs font-bold">{(r.name || "?")[0]?.toUpperCase()}</div>
                    )}
                    <div className="flex-1 min-w-0">
                      <Link to={`/membros/${r.slug || r.user_id}`} className="font-display font-semibold text-sm line-through text-zinc-400 hover:text-white">
                        {r.name}
                      </Link>
                      <div className="text-xs text-red-400 mt-0.5">Motivo: {r.reason}</div>
                      <div className="text-[10px] text-zinc-600 font-mono-accent">{fmtDateTime(r.rejected_at)}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="mt-8">
            {!user ? (
              <>
                <Button onClick={() => navigate("/registar")} className="btn-crimson w-full h-14 text-base font-display font-bold" data-testid="join-register-btn">
                  <Gift size={18} weight="bold" className="mr-2" /> Participar no sorteio
                </Button>
                <div className="mt-3 text-xs text-zinc-500 flex items-center gap-2">
                  <UserCircle size={14} weight="bold" /> Só é possível participar se tiver sessão iniciada.
                </div>
              </>
            ) : g.am_joined ? (
              <div className="text-center py-2">
                <div className="inline-flex items-center gap-2 text-emerald-400 mb-1">
                  <Check size={18} weight="bold" /> <span className="font-display font-semibold">Estás a participar — boa sorte!</span>
                </div>
                <div className="text-xs text-zinc-500 mt-1">A tua participação foi confirmada.</div>
              </div>
            ) : s.canJoin ? (
              <Button onClick={join} disabled={busy} className="btn-crimson w-full h-14 text-base font-display font-bold" data-testid="join-btn">
                <Gift size={18} weight="bold" className="mr-2" /> Participar no sorteio
              </Button>
            ) : (
              <div className="text-sm text-zinc-500">Participação encerrada.</div>
            )}
          </div>

          {/* Participants pills */}
          {(g.participants?.length > 0 || isAdmin) && (
            <div className="mt-8 pt-6 border-t border-white/5">
              <div className="flex items-center justify-between mb-3">
                <div className="font-mono-accent text-[10px] text-zinc-500">PARTICIPANTES ({g.participants?.length || 0})</div>
                {isAdmin && (
                  <button onClick={() => { setAddMemberOpen(true); setMemberQuery(""); }} className="text-xs text-[#E63946] hover:underline font-display font-semibold flex items-center gap-1" data-testid="admin-add-participant-btn">
                    <UserPlus size={12} weight="bold" /> Adicionar membro
                  </button>
                )}
              </div>
              <div className="flex flex-wrap gap-2" data-testid="participants-list">
                {(g.participants || []).slice(0, 30).map(p => (
                  <div key={p.user_id} className="inline-flex items-center gap-2 bg-[#1C1C22] border border-white/5 rounded-full pl-1 pr-1 py-1 text-xs" data-testid={`participant-${p.user_id}`}>
                    <Link to={`/membros/${p.slug || p.user_id}`} className="flex items-center gap-2 hover:text-[#E63946] pr-2">
                      {p.picture ? (
                        <img src={resolveImageUrl(p.picture)} alt="" className="w-6 h-6 rounded-full object-cover" />
                      ) : (
                        <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-[10px] font-bold">{(p.name || "?")[0]?.toUpperCase()}</div>
                      )}
                      <span className="text-zinc-300">{p.name}</span>
                    </Link>
                    {isAdmin && (
                      <button onClick={() => adminRemoveParticipant(p.user_id)} className="w-5 h-5 rounded-full grid place-items-center text-zinc-500 hover:text-red-400 hover:bg-red-500/10" title="Remover" data-testid={`admin-remove-${p.user_id}`}>
                        <X size={11} weight="bold" />
                      </button>
                    )}
                  </div>
                ))}
                {(g.participants?.length || 0) > 30 && (
                  <span className="text-xs text-zinc-500 self-center">+{g.participants.length - 30} outros</span>
                )}
                {(g.participants?.length || 0) === 0 && (
                  <span className="text-xs text-zinc-500 italic">Ainda sem participantes</span>
                )}
              </div>
            </div>
          )}
        </div>

        {/* SIDEBAR */}
        <aside className="space-y-5" data-testid="sorteio-sidebar">
          <SidebarCard title="Detalhes do sorteio">
            <SidebarRow icon={<Calendar size={16} weight="bold" />} label="Início" value={fmtDate(g.starts_at || g.created_at)} />
            <SidebarRow icon={<Clock size={16} weight="bold" />} label="Termina" value={fmtDateTime(g.draw_at)} />
            <SidebarRow icon={<Trophy size={16} weight="bold" />} label="Anúncio do vencedor" value={fmtDate(g.winner_announcement_at || g.draw_at)} />
            {g.prize && <SidebarRow icon={<Gift size={16} weight="bold" />} label="Prémio" value={g.prize} />}
            {g.eligibility && <SidebarRow icon={<UserCircle size={16} weight="bold" />} label="Elegibilidade" value={g.eligibility} />}
          </SidebarCard>

          {(g.rules && g.rules.length > 0) && (
            <SidebarCard title="Regras do sorteio">
              <ul className="space-y-2.5" data-testid="sorteio-rules">
                {g.rules.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-300 leading-snug">
                    <CheckCircle size={16} weight="fill" className="text-[#E63946] flex-shrink-0 mt-0.5" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </SidebarCard>
          )}
        </aside>
      </div>

      {/* Transparency banner */}
      <div className="mt-6 bg-[#0f0f14] border border-white/5 rounded-2xl p-5 flex items-start gap-4 flex-wrap">
        <div className="w-10 h-10 rounded-lg bg-[#E63946]/10 text-[#E63946] grid place-items-center flex-shrink-0">
          <ShieldCheck size={20} weight="fill" />
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="font-display font-bold">Transparência e confiança</div>
          <div className="text-sm text-zinc-400 mt-1">Todos os sorteios são realizados de forma transparente e justa. O vencedor é escolhido aleatoriamente através de um sistema automático.</div>
        </div>
        <Link to="/sorteio" className="text-sm text-[#E63946] hover:underline font-display font-semibold flex items-center gap-1 flex-shrink-0">
          Ver vencedores anteriores <CaretRight size={14} weight="bold" />
        </Link>
      </div>

      <Dialog open={rejectOpen} onOpenChange={setRejectOpen}>
        <DialogContent className="bg-[#121217] border-white/10 text-white">
          <DialogHeader><DialogTitle className="font-display text-2xl">Recusar Vencedor</DialogTitle></DialogHeader>
          <div className="text-sm text-zinc-400 mb-3">Este utilizador não será selecionado novamente. O motivo ficará visível.</div>
          <Textarea rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Ex: utilizador banido, não cumpre requisitos, etc." className="bg-[#1C1C22] border-white/10" data-testid="reject-reason-input" />
          <div className="flex gap-2 mt-4">
            <Button type="button" variant="outline" className="flex-1 border-white/15" onClick={() => setRejectOpen(false)}>Cancelar</Button>
            <Button type="button" className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={reject} disabled={busy} data-testid="reject-confirm-btn">Confirmar Recusa</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Admin: add participant */}
      <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
        <DialogContent className="bg-[#121217] border-white/10 text-white max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><UserPlus size={22} weight="bold" /> Adicionar Membro</DialogTitle></DialogHeader>
          <div className="relative mt-2">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
            <Input value={memberQuery} onChange={(e) => setMemberQuery(e.target.value)} placeholder="Pesquisar por nome ou email..." className="pl-9 bg-[#1C1C22] border-white/10" data-testid="participant-member-search" />
          </div>
          <div className="mt-3 space-y-1 max-h-72 overflow-y-auto">
            {members.map(m => {
              const already = (g.participants || []).some(p => p.user_id === m.user_id);
              return (
                <div key={m.user_id} className="flex items-center gap-3 bg-[#1C1C22] border border-white/5 rounded-lg px-3 py-2" data-testid={`admin-member-row-${m.user_id}`}>
                  {m.picture ? (
                    <img src={resolveImageUrl(m.picture)} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E63946] to-[#7209B7] grid place-items-center text-xs font-bold flex-shrink-0">{(m.name || "?")[0]?.toUpperCase()}</div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm truncate">{m.name || "—"}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{m.user_id}</div>
                  </div>
                  {already ? (
                    <span className="text-[10px] text-emerald-400 font-mono-accent">JÁ PARTICIPA</span>
                  ) : (
                    <button onClick={() => adminAddParticipant(m.user_id)} className="text-xs px-3 py-1.5 rounded btn-crimson flex items-center gap-1" data-testid={`admin-add-member-${m.user_id}`}>
                      <UserPlus size={12} weight="bold" /> Adicionar
                    </button>
                  )}
                </div>
              );
            })}
            {members.length === 0 && (
              <div className="text-center py-6 text-xs text-zinc-500">Sem resultados</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MiniStat({ icon, value, sub, label, testid }) {
  return (
    <div className="flex items-center gap-3" data-testid={testid}>
      <div className="w-10 h-10 rounded-full bg-[#E63946]/10 text-[#E63946] grid place-items-center flex-shrink-0">
        {icon}
      </div>
      <div className="min-w-0">
        <div className="font-display text-lg font-extrabold leading-tight">{value}</div>
        {sub && <div className="text-xs text-zinc-400 truncate">{sub}</div>}
        <div className="text-[11px] text-zinc-500 font-mono-accent truncate">{label}</div>
      </div>
    </div>
  );
}

function SidebarCard({ title, children }) {
  return (
    <div className="bg-[#0f0f14] border border-white/5 rounded-2xl p-5">
      <div className="font-display font-bold text-lg mb-4">{title}</div>
      <div className="space-y-3.5">{children}</div>
    </div>
  );
}

function SidebarRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-[#E63946] mt-0.5">{icon}</div>
      <div className="flex-1 min-w-0 flex items-start justify-between gap-3">
        <div className="text-zinc-400 text-sm">{label}</div>
        <div className="text-white text-sm text-right font-display font-medium">{value}</div>
      </div>
    </div>
  );
}
